import { Heart, Dumbbell, Leaf, Sun } from "lucide-react";
import { motion } from "framer-motion";
import { Btn, Eyebrow, HeroImage, MaskTitle, Reveal } from "@/components/site/Reveal";
import FeatureStrip from "@/components/site/FeatureStrip";
import { CORE_FEATURES } from "./Home";

const BELIEFS = [
  { icon: Heart, title: ["Better", "hydration"], text: "Helps your body perform at its best." },
  { icon: Dumbbell, title: ["Supports", "wellness"], text: "A key part of a healthy, active lifestyle." },
  { icon: Leaf, title: ["Cleaner", "ingredients"], text: "Removes harmful contaminants." },
  { icon: Sun, title: ["A brighter", "tomorrow"], text: "Healthier families and stronger communities." },
];

export default function About() {
  return (
    <>
      <section data-testid="about-hero" className="grid lg:grid-cols-[45%_55%] min-h-[580px] border-b border-[var(--line)]">
        <div className="flex flex-col justify-center px-5 sm:px-8 lg:pl-12 lg:pr-10 xl:pl-[max(3rem,calc((100vw-1280px)/2+3rem))] py-14 lg:py-20">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow">About Us</motion.p>
          <MaskTitle testId="about-hero-title" lines={["A few bros.", "A bigger", "purpose."]} className="mt-5 text-[2.6rem] sm:text-6xl xl:text-[4.25rem]" />
          <Reveal delay={0.5}>
            <p className="mt-6 text-[17px] sm:text-lg text-[var(--body)] leading-relaxed max-w-md">Water System Bros. was founded by a group of bros who had an idea — to provide high end water purification equipment to homeowners throughout San Diego. What started as a simple idea has grown into a mission to help families get access to cleaner, healthier water.</p>
            <Btn to="/products" testId="about-products-btn" className="mt-8">Our Products</Btn>
          </Reveal>
        </div>
        <HeroImage testId="about-hero-image" src="/img/bros.jpg" alt="The Water System Bros. team overlooking San Diego" className="min-h-[360px] lg:min-h-full" />
      </section>

      <FeatureStrip items={CORE_FEATURES} testId="about-feature-strip" />

      <section data-testid="founder-section" className="bg-[var(--soft)] py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal className="relative aspect-[3/2] w-full overflow-hidden bg-[#111]">
            <img data-testid="founder-photo" src="/img/thomas.jpg" alt="Thomas Pavel, founder of Water System Bros., in branded gear" className="absolute inset-0 h-full w-full object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Our Founder</Eyebrow>
            <h2 data-testid="founder-name" className="headline mt-4 text-4xl sm:text-5xl">Thomas Pavel</h2>
            <p className="font-head font-extrabold uppercase text-xl sm:text-2xl mt-2 tracking-[-0.01em]">Third generation water guru</p>
            <p className="mt-5 text-[16px] text-[var(--body)] leading-relaxed">Thomas Pavel is a third generation water guru, continuing a family legacy in the water industry. Growing up around water treatment and purification, he developed a deep understanding of the technology and a passion for helping people live healthier lives.</p>
            <p className="mt-4 text-[16px] text-[var(--body)] leading-relaxed">Thomas has always had a strong passion for health and wellness, and believes that clean, pure water is a foundation for a healthier lifestyle. This passion, combined with his experience and knowledge, is what drives Water System Bros. today.</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="belief-section" className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Our Belief</Eyebrow>
            <h2 data-testid="belief-title" className="headline mt-4 text-4xl sm:text-5xl">Pure water fuels<br />a healthier life.</h2>
            <p className="mt-5 text-[17px] text-[var(--body)] leading-relaxed">We believe that pure water and health are deeply connected. Clean, high quality water helps your body function at its best, supports better hydration, improves overall wellness, and contributes to a higher quality of life. When you remove contaminants and enjoy better-tasting water, it's easier to make healthier choices every day.</p>
          </Reveal>
          <Reveal delay={0.1} className="bg-[var(--soft)] grid grid-cols-1 sm:grid-cols-2">
            {BELIEFS.map((b, i) => (
              <div key={b.title.join()} data-testid={`belief-${i}`} className={`flex gap-5 p-7 lg:p-8 border-[var(--line)] ${i % 2 === 0 ? "sm:border-r" : ""} ${i < 2 ? "border-b" : i === 2 ? "border-b sm:border-b-0" : ""}`}>
                <b.icon className="h-10 w-10 shrink-0" strokeWidth={1.4} />
                <div>
                  <h3 className="font-head font-bold uppercase text-[13px] leading-snug">{b.title[0]}<br />{b.title[1]}</h3>
                  <p className="mt-2 text-[14px] text-[var(--body)] leading-snug">{b.text}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section data-testid="community-section" className="grid lg:grid-cols-[55%_45%] bg-white">
        <Reveal y={0} className="relative min-h-[320px] lg:min-h-[460px] overflow-hidden">
          <img src="/img/skyline.jpg" alt="San Diego skyline across the bay at sunset" className="absolute inset-0 h-full w-full object-cover" />
        </Reveal>
        <div className="flex flex-col justify-center px-5 sm:px-8 lg:px-14 py-14">
          <Reveal>
            <Eyebrow>Rooted in San Diego</Eyebrow>
            <h2 data-testid="community-title" className="headline mt-4 text-4xl sm:text-5xl">Built for<br />our community.</h2>
            <p className="mt-5 text-[16px] text-[var(--body)] leading-relaxed max-w-md">We're proud to call San Diego home. From the beaches to the neighborhoods, our goal is to help local homeowners get access to high quality, purified water. We're more than just a water company — we're a group of bros on a mission to make San Diego a healthier place to live.</p>
            <Btn to="/signup" testId="community-signup-btn" className="mt-8">Sign Up Today</Btn>
          </Reveal>
        </div>
      </section>
    </>
  );
}
